import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus, Search, Filter, Loader2, MoreHorizontal, Pencil, Trash2, CalendarClock,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreditCardVisual } from '@/components/cards/CreditCardVisual';
import { Cards as CardsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR } from '@/lib/format';
import { toast } from 'sonner';


// Inline edit dialog — covers the user-owned fields you can actually change
// after a card is in the wallet. Reward DSL, fees, tiers, milestone definitions
// remain operator-owned (admin) and are not editable here.
const EditCardDialog = ({ card, open, onOpenChange, onSaved }) => {
  const [nickname, setNickname] = useState('');
  const [last4, setLast4] = useState('');
  const [approvedAt, setApprovedAt] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [currentRewardPoints, setCurrentRewardPoints] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!card) return;
    setNickname(card.nickname || '');
    setLast4(card.last4 || '');
    setApprovedAt(card.approved_at || '');
    setCreditLimit(card.credit_limit_inr != null ? String(card.credit_limit_inr) : '');
    setIsActive(card.is_active !== false);
    setCurrentRewardPoints(
      card.current_reward_points != null ? String(card.current_reward_points) : ''
    );
  }, [card]);

  const submit = async () => {
    if (!card) return;
    setSaving(true);
    try {
      const payload = {
        nickname: nickname.trim() || null,
        last4: last4 && /^\d{4}$/.test(last4) ? last4 : null,
        approved_at: approvedAt || null,
        credit_limit_inr: creditLimit ? Number(creditLimit) : null,
        is_active: isActive,
        current_reward_points: currentRewardPoints
          ? Number(currentRewardPoints)
          : 0,
      };
      await CardsApi.update(card.id, payload);
      toast.success('Card updated');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.message || 'Could not update card');
    } finally {
      setSaving(false);
    }
  };

  if (!card) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-card-dialog">
        <DialogHeader>
          <DialogTitle>Edit {card.bank} {card.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nickname</Label>
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder='e.g. "Travel main"'
              className="h-10 mt-1"
              data-testid="edit-card-nickname"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Last 4 digits</Label>
              <Input
                inputMode="numeric"
                maxLength={4}
                value={last4}
                onChange={(e) =>
                  setLast4(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))
                }
                className="h-10 mt-1 font-mono-num"
                data-testid="edit-card-last4"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                Approved on
                {card.joining_date_is_estimate ? (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium"
                    data-testid="edit-card-joining-date-estimated-badge"
                  >
                    Estimated
                  </span>
                ) : (card.approved_at ? (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium"
                    data-testid="edit-card-joining-date-confirmed-badge"
                  >
                    Confirmed
                  </span>
                ) : null)}
              </Label>
              <Input
                type="date"
                value={approvedAt ? approvedAt.slice(0, 10) : ''}
                onChange={(e) => setApprovedAt(e.target.value || '')}
                className="h-10 mt-1"
                data-testid="edit-card-approved-at"
              />
              {card.joining_date_is_estimate && (
                <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400" data-testid="edit-card-joining-date-estimate-hint">
                  Saving a date here will unlock anniversary &amp; welcome milestones.
                </div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Credit limit (₹)</Label>
            <Input
              inputMode="numeric"
              value={creditLimit}
              onChange={(e) =>
                setCreditLimit(e.target.value.replace(/[^0-9]/g, ''))
              }
              placeholder="e.g. 500000"
              className="h-10 mt-1 font-mono-num"
              data-testid="edit-card-credit-limit"
            />
          </div>
          <div>
            <Label className="text-xs">Current reward balance</Label>
            <Input
              inputMode="numeric"
              value={currentRewardPoints}
              onChange={(e) =>
                setCurrentRewardPoints(e.target.value.replace(/[^0-9.]/g, ''))
              }
              placeholder="e.g. 24500"
              className="h-10 mt-1 font-mono-num"
              data-testid="edit-card-current-points"
            />
            <div className="mt-1 text-[10px] text-muted-foreground">
              In {card.currency || 'Reward Points'}. Edit milestone progress + current tier on the Milestones page.
            </div>
          </div>
          <label className="flex items-center justify-between rounded-md border border-border bg-[hsl(var(--surface-2))] px-3 py-2 cursor-pointer">
            <div>
              <div className="text-xs font-medium">Active card</div>
              <div className="text-[10px] text-muted-foreground">
                Inactive cards are skipped during recommendations.
              </div>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
              data-testid="edit-card-is-active"
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="gap-1.5"
            data-testid="edit-card-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default function Cards() {
  const navigate = useNavigate();
  const { user, demoLogin } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (!user) await demoLogin();
      const data = await CardsApi.mine();
      setCards(data || []);
    } catch (_) {
      try { setCards((await CardsApi.library()) || []); } catch (__) {}
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  const filtered = cards.filter((c) => {
    const q = !query || `${c.bank} ${c.name}`.toLowerCase().includes(query.toLowerCase());
    const t = type === 'all' || c.type === type;
    return q && t;
  });
  const totalFees = cards.reduce((s, c) => s + (c.annual_fee || 0), 0);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await CardsApi.remove(deleteTarget.id);
      toast.success(`Removed ${deleteTarget.bank} ${deleteTarget.name}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not remove card');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start sm:items-end justify-between flex-col sm:flex-row gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">Wallet</div>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">My Cards</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {cards.length} active cards · total annual fees {formatINR(totalFees)}
          </p>
        </div>
        <Button onClick={() => navigate('/cards/add')} data-testid="cards-add-card-button" className="gap-2">
          <Plus className="h-4 w-4" /> Add card
        </Button>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by bank or card name..."
            className="pl-10 h-10 border-transparent bg-[hsl(var(--surface-2))]"
          />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-10 sm:w-[180px] gap-2"><Filter className="h-4 w-4" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="rewards">Reward Points</SelectItem>
            <SelectItem value="miles">Airline Miles</SelectItem>
            <SelectItem value="cashback">Cashback</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <div className="font-medium">No cards in your wallet yet</div>
          <div className="text-xs text-muted-foreground mt-1">Tap "Add card" to start tracking rewards across your portfolio.</div>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((card, i) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24, delay: i * 0.05 }}
              className="flex flex-col gap-3"
            >
              <CreditCardVisual card={card} size="md" onClick={() => navigate(`/cards/${card.id}`)} />
              <Card className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold tracking-tight truncate">{card.bank} — {card.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{card.currency}</span>
                      {card.joining_date_is_estimate && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditing(card); }}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium hover:bg-amber-500/20 transition-colors"
                          data-testid={`card-joining-date-estimated-${card.id}`}
                          title="Set joining date to unlock anniversary and welcome milestones"
                        >
                          <CalendarClock className="h-2.5 w-2.5" /> Joining date
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline">{card.tier}</Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-full"
                          aria-label="Card actions"
                          data-testid={`card-actions-${card.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); setEditing(card); }}
                          className="cursor-pointer"
                          data-testid={`card-edit-${card.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); setDeleteTarget(card); }}
                          className="cursor-pointer text-rose-500 focus:text-rose-500"
                          data-testid={`card-remove-${card.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground">Annual fee</div>
                    <div className="mt-0.5 font-mono-num font-medium">{formatINR(card.annual_fee)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Best for</div>
                    <div className="mt-0.5 font-medium truncate">{(card.best_for || [])[0] || '—'}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/cards/${card.id}`)}
                    data-testid={`card-view-${card.id}`}
                  >
                    View detail
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(card)}
                    className="gap-1.5"
                    data-testid={`card-edit-btn-${card.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <EditCardDialog
        card={editing}
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        onSaved={load}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v && !deleting) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this card?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  This unlinks <b>{deleteTarget.bank} {deleteTarget.name}</b> from your wallet. Past transactions on this card stay in your log but won&rsquo;t be considered for recommendations. You can add the card back any time.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="card-remove-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Remove card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
